import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{title}</h2>
    <div className="space-y-2 text-neutral-600 dark:text-neutral-400 leading-relaxed">
      {children}
    </div>
  </section>
)

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-surface-50 dark:bg-dark-300 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Back link */}
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to login
        </Link>

        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
            Terms &amp; Conditions
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-500">Last updated: 22 March 2026</p>
        </div>

        <div className="bg-white dark:bg-dark-200 rounded-2xl border border-neutral-200 dark:border-neutral-700 shadow-sm p-8 space-y-8">

          <Section title="1. Agreement">
            <p>
              By creating a WebNest account and using our services, you agree to these Terms &amp; Conditions.
              If you do not agree, do not use the service.
            </p>
          </Section>

          <Section title="2. The Service">
            <p>
              WebNest provides a cloud-based platform allowing clients to manage, edit, and publish websites
              built and managed by WebNest. The platform includes a content editor, media library, and
              support system.
            </p>
          </Section>

          <Section title="3. Your Account">
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>You are responsible for keeping your login credentials secure</li>
              <li>You must not share your account with others</li>
              <li>You must provide accurate information when creating your account</li>
              <li>You must be at least 18 years old to use the service</li>
              <li>We reserve the right to suspend or terminate accounts that violate these terms</li>
            </ul>
          </Section>

          <Section title="4. Acceptable Use">
            <p>You agree not to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Upload content that is illegal, harmful, defamatory, or infringes third-party rights</li>
              <li>Attempt to gain unauthorised access to the platform or other users' accounts</li>
              <li>Use the service to send spam or unsolicited communications</li>
              <li>Reverse engineer or attempt to extract the source code of the platform</li>
            </ul>
            <p>You are solely responsible for all content you upload or publish through WebNest.</p>
          </Section>

          <Section title="5. Subscription and Billing">
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>WebNest is offered on a subscription basis, billed monthly or annually</li>
              <li>Payments are processed securely by Stripe</li>
              <li>Subscriptions automatically renew unless cancelled before the renewal date</li>
              <li>All prices are shown in GBP and are inclusive of any applicable taxes unless stated otherwise</li>
              <li>We reserve the right to change pricing with 30 days' notice</li>
            </ul>
          </Section>

          <Section title="6. Cancellation and Refunds">
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>You may cancel your subscription at any time from your account settings</li>
              <li>Upon cancellation, you retain access until the end of your current billing period</li>
              <li>We do not offer refunds for partial billing periods</li>
              <li>
                In exceptional circumstances, refund requests can be submitted to{' '}
                <a href="mailto:hello@marcoalcini.com" className="text-accent-600 dark:text-accent-400 hover:underline">
                  hello@marcoalcini.com
                </a>{' '}
                and will be reviewed on a case-by-case basis
              </li>
            </ul>
          </Section>

          <Section title="7. Your Content">
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>You retain full ownership of the content you upload to WebNest</li>
              <li>
                By uploading content, you grant WebNest a limited licence to store, display, and deliver
                that content solely for the purpose of providing the service
              </li>
              <li>We will never use your content for marketing or share it with third parties without your consent</li>
            </ul>
          </Section>

          <Section title="8. Service Availability">
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>We aim to maintain high availability of the platform but do not guarantee 100% uptime</li>
              <li>Scheduled maintenance will be communicated in advance where possible</li>
              <li>We are not liable for losses resulting from temporary unavailability of the service</li>
            </ul>
          </Section>

          <Section title="9. Limitation of Liability">
            <p>To the fullest extent permitted by law:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>WebNest is not liable for any indirect, incidental, or consequential loss</li>
              <li>Our total liability to you in any month shall not exceed the amount you paid us in that month</li>
            </ul>
          </Section>

          <Section title="10. Intellectual Property">
            <p>
              The WebNest platform, design, and software remain the intellectual property of WebNest.
              Nothing in these terms transfers ownership of the platform to you.
            </p>
          </Section>

          <Section title="11. Governing Law">
            <p>
              These terms are governed by the laws of England and Wales. Any disputes shall be subject
              to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </Section>

          <Section title="12. Changes to These Terms">
            <p>
              We may update these terms from time to time. We will notify you by email at least 14 days
              before any material changes take effect. Continued use of the service after that date
              constitutes acceptance.
            </p>
          </Section>

          <Section title="13. Contact">
            <p>
              For any questions regarding these terms, contact us at{' '}
              <a href="mailto:hello@marcoalcini.com" className="text-accent-600 dark:text-accent-400 hover:underline">
                hello@marcoalcini.com
              </a>
              .
            </p>
          </Section>

        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-neutral-500 dark:text-neutral-500 space-x-4">
          <Link to="/privacy" className="hover:text-accent-600 dark:hover:text-accent-400 transition-colors">
            Privacy Policy
          </Link>
          <span>·</span>
          <Link to="/login" className="hover:text-accent-600 dark:hover:text-accent-400 transition-colors">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  )
}

export default TermsAndConditions
